import { useCallback, useEffect, useRef, useState } from 'react';
import type { Stage, StageParticipantInfo, StageStream } from 'amazon-ivs-web-broadcast';

/**
 * Joining an IVS Real-Time stage, as one hook, for both sides of a show.
 *
 * **One hook rather than a publisher one and a viewer one**, because the difference is a
 * single line of strategy — whether we publish — and two hooks would mean two
 * lifecycles to get wrong. The lifecycle is the hard part here: a `Stage` holds a
 * WebRTC connection and a camera, and React in development mounts every effect twice, so
 * anything that joins on mount and leaves on unmount has to survive being told to do
 * both immediately.
 *
 * **The camera is opened by the hook and closed by the hook.** A `MediaStreamTrack` that
 * outlives its component is a camera light that stays on after the seller navigates away
 * — the single most alarming bug this screen could ship with.
 *
 * **Nothing here retries.** A token expires, a network drops, a stage is deleted when the
 * show ends; all three surface as a state the screen can render and a button the person
 * can press. A hidden reconnect loop against a credentialed video service is a way to
 * spend money in a tab nobody is looking at.
 *
 * **The SDK is imported dynamically**, and that is worth a line: it is ~700 kB, and it was
 * landing in the bundle every guest downloads to look at a feed. Only a show screen ever
 * calls this hook, so only a show screen pays for it. The type-only import above is erased
 * at compile time and pulls nothing in.
 */

export type StageStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface StageSession {
  status: StageStatus;
  error?: string;
  /** Remote participants' media, ready to attach to a `<video>`. Keyed by participant. */
  remote: { id: string; stream: MediaStream }[];
  /** The seller's own camera, for the self-preview. Undefined for a viewer. */
  local?: MediaStream;
  leave: () => void;
}

export function useStage(input: { token: string | undefined; publish: boolean }): StageSession {
  const [status, setStatus] = useState<StageStatus>('idle');
  const [error, setError] = useState<string>();
  const [remote, setRemote] = useState<{ id: string; stream: MediaStream }[]>([]);
  const [local, setLocal] = useState<MediaStream>();

  const stageRef = useRef<Stage | undefined>(undefined);
  const cameraRef = useRef<MediaStream | undefined>(undefined);
  const { token, publish } = input;

  const teardown = useCallback(() => {
    stageRef.current?.leave();
    stageRef.current = undefined;
    // The camera light goes out here, and nowhere else.
    for (const track of cameraRef.current?.getTracks() ?? []) track.stop();
    cameraRef.current = undefined;
    setLocal(undefined);
    setRemote([]);
    setStatus('idle');
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setStatus('connecting');
    setError(undefined);

    void (async () => {
      try {
        const { LocalStageStream, Stage, StageConnectionState, StageEvents, SubscribeType } =
          await import('amazon-ivs-web-broadcast');
        if (cancelled) return;

        let publishing: InstanceType<typeof LocalStageStream>[] = [];
        if (publish) {
          const camera = await navigator.mediaDevices.getUserMedia({
            // 720p: a live show is watched on a phone, and asking for 1080p from a laptop
            // webcam costs upload bandwidth nobody sees.
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: true,
          });
          if (cancelled) {
            for (const track of camera.getTracks()) track.stop();
            return;
          }
          cameraRef.current = camera;
          setLocal(camera);
          publishing = camera.getTracks().map((track) => new LocalStageStream(track));
        }

        const stage = new Stage(token, {
          stageStreamsToPublish: () => publishing,
          shouldPublishParticipant: () => publish,
          // Everyone else on the stage, which for a show is the seller and any guests
          // they bring on. A viewer publishes nothing, so viewers never see each other.
          shouldSubscribeToParticipant: () => SubscribeType.AUDIO_VIDEO,
        });

        stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state) => {
          if (cancelled) return;
          if (state === StageConnectionState.CONNECTED) setStatus('connected');
          if (state === StageConnectionState.ERRORED) {
            setStatus('error');
            setError('The video connection dropped.');
          }
        });

        stage.on(
          StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED,
          (participant: StageParticipantInfo, streams: StageStream[]) => {
            if (cancelled) return;
            const media = new MediaStream(streams.map((stream) => stream.mediaStreamTrack));
            setRemote((current) => [
              ...current.filter((entry) => entry.id !== participant.id),
              { id: participant.id, stream: media },
            ]);
          },
        );

        stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant: StageParticipantInfo) => {
          if (cancelled) return;
          setRemote((current) => current.filter((entry) => entry.id !== participant.id));
        });

        stageRef.current = stage;
        await stage.join();
        if (cancelled) stage.leave();
      } catch (caught) {
        if (cancelled) return;
        setStatus('error');
        setError(describeStageError(caught));
      }
    })();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [token, publish, teardown]);

  return { status, ...(error !== undefined ? { error } : {}), remote, ...(local ? { local } : {}), leave: teardown };
}

/**
 * The three failures worth naming, because each has a different thing for the person to
 * do — and the browser's own messages for them are unhelpful in three different ways.
 */
function describeStageError(caught: unknown): string {
  const name = caught instanceof Error ? caught.name : '';
  if (name === 'NotAllowedError')
    return 'Camera and microphone access was refused. Allow it in the browser, then try again.';
  if (name === 'NotFoundError') return 'No camera or microphone was found on this device.';
  if (name === 'NotReadableError') return 'The camera is in use by another app. Close it and try again.';
  return caught instanceof Error ? caught.message : 'The stage could not be joined.';
}
